/**
 * Image Error Handler Plugin for Native Interactive Site Map (ISM)
 *
 * Handles broken image errors in card components by replacing them with a placeholder image.
 * Listens for image load failures and replaces broken images with the placeholder from IRSDomain.
 *
 * Lifecycle:
 * - On start: Waits for runtime configuration to be available, then binds error handlers
 * - On stop: Unbinds error handlers and cleans up subscriptions
 *
 * @module nativeInteractiveSiteMap/plugins/imageError.plugin
 */

import { take } from 'rxjs/operators';
import createImageErrorHandler from '../handlers/imageErrorHandler';

export default function createImageErrorPlugin() {
  const state = {
    imageErrorHandler: null,
    subscriptions: [],
  };

  return {
    name: 'image-error-handler',

    async init(context) {
      context.state.imageErrorHandler = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.imageErrorHandler?.started) {
        return;
      }

      // Find the panel container (try multiple selectors in priority order)
      const panel = context.root.querySelector('[data-panel]');
      
      if (!panel) {
        console.warn('Image error handler: Could not find panel container in ISM root');
        return;
      }

      context.state.imageErrorHandler = { started: true, ready: false };

      // Subscribe to configuration$ (emits once when runtime config is available)
      const configurationSubscription = context.services.configuration$
        .pipe(take(1))
        .subscribe(({ imageResizeService }) => {
          const placeholderImageUrl = `${imageResizeService}/images/placeholder.png`;
          const imageErrorHandler = createImageErrorHandler(panel, placeholderImageUrl);
          state.imageErrorHandler = imageErrorHandler;
          context.state.imageErrorHandler = { started: true, ready: true };
        });

      state.subscriptions.push(configurationSubscription);
    },

    async stop(context) {
      if (state.imageErrorHandler && typeof state.imageErrorHandler.unbind === 'function') {
        state.imageErrorHandler.unbind();
        state.imageErrorHandler = null;
      }

      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      context.state.imageErrorHandler = { started: false, ready: false };
    },
  };
}
